import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Tooltip,
  Fab,
  InputAdornment,
  Pagination,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  MedicalServices,
  AttachMoney,
  LocationOn,
  CheckCircle,
  Warning,
  Info,
  Search,
  FilterList,
  Refresh,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listMedicalEquipment,
  createMedicalEquipment,
  updateMedicalEquipment,
  deleteMedicalEquipment,
  getAvailableEquipment,
  getInUseEquipment,
} from "../../../../api/medicalEquipment";

const MedicalEquipmentPage = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const queryClient = useQueryClient();

  // State
  const [equipmentDialogOpen, setEquipmentDialogOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(12);
  const [equipmentFormData, setEquipmentFormData] = useState({
    name: "",
    description: "",
    equipment_type: 6,
    charge_type: 1,
    hourly_rate: 0,
    daily_rate: 0,
    per_use_rate: 0,
    fixed_rate: 0,
    location: "",
    serial_number: "",
    notes: "",
  });

  // Fetch data
  const { data: equipmentData, isLoading: equipmentLoading } = useQuery({
    queryKey: ["medical-equipment", page, searchTerm, statusFilter, typeFilter],
    queryFn: () =>
      listMedicalEquipment(
        {
          page: page,
          page_size: rowsPerPage,
          search: searchTerm,
          status: statusFilter !== "all" ? statusFilter : undefined,
          equipment_type: typeFilter !== "all" ? typeFilter : undefined,
        },
        token
      ),
  });

  const { data: availableEquipment } = useQuery({
    queryKey: ["available-equipment"],
    queryFn: () => getAvailableEquipment(token),
  });

  const { data: inUseEquipment } = useQuery({
    queryKey: ["in-use-equipment"],
    queryFn: () => getInUseEquipment(token),
  });

  // Mutations
  const createEquipmentMutation = useMutation({
    mutationFn: (data) => createMedicalEquipment(data, token),
    onSuccess: () => {
      queryClient.invalidateQueries(["medical-equipment"]);
      queryClient.invalidateQueries(["available-equipment"]);
      queryClient.invalidateQueries(["in-use-equipment"]);
      setEquipmentDialogOpen(false);
      resetForm();
    },
  });

  const updateEquipmentMutation = useMutation({
    mutationFn: ({ id, data }) => updateMedicalEquipment(id, data, token),
    onSuccess: () => {
      queryClient.invalidateQueries(["medical-equipment"]);
      queryClient.invalidateQueries(["available-equipment"]);
      queryClient.invalidateQueries(["in-use-equipment"]);
      setEquipmentDialogOpen(false);
      resetForm();
    },
  });

  const deleteEquipmentMutation = useMutation({
    mutationFn: (id) => deleteMedicalEquipment(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries(["medical-equipment"]);
      queryClient.invalidateQueries(["available-equipment"]);
      queryClient.invalidateQueries(["in-use-equipment"]);
    },
  });

  // Helper functions
  const resetForm = () => {
    setEquipmentFormData({
      name: "",
      description: "",
      equipment_type: 6,
      charge_type: 1,
      hourly_rate: 0,
      daily_rate: 0,
      per_use_rate: 0,
      fixed_rate: 0,
      location: "",
      serial_number: "",
      notes: "",
    });
    setSelectedEquipment(null);
  };

  const handleEditEquipment = (equipment) => {
    setSelectedEquipment(equipment);
    setEquipmentFormData({
      name: equipment.name,
      description: equipment.description || "",
      equipment_type: equipment.equipment_type,
      charge_type: equipment.charge_type,
      hourly_rate: equipment.hourly_rate,
      daily_rate: equipment.daily_rate,
      per_use_rate: equipment.per_use_rate,
      fixed_rate: equipment.fixed_rate,
      location: equipment.location || "",
      serial_number: equipment.serial_number || "",
      notes: equipment.notes || "",
    });
    setEquipmentDialogOpen(true);
  };

  const handleAddEquipment = () => {
    resetForm();
    setEquipmentDialogOpen(true);
  };

  const handleSubmitEquipment = () => {
    if (selectedEquipment) {
      updateEquipmentMutation.mutate({
        id: selectedEquipment.id,
        data: equipmentFormData,
      });
    } else {
      createEquipmentMutation.mutate(equipmentFormData);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 1:
        return "success"; // Available
      case 2:
        return "warning"; // In Use
      case 3:
        return "info"; // Maintenance
      case 4:
        return "error"; // Out of Order
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 1:
        return "Available";
      case 2:
        return "In Use";
      case 3:
        return "Maintenance";
      case 4:
        return "Out of Order";
      default:
        return "Unknown";
    }
  };

  const getEquipmentTypeText = (type) => {
    switch (type) {
      case 1:
        return "Respiratory";
      case 2:
        return "Cardiovascular";
      case 3:
        return "Monitoring";
      case 4:
        return "Therapeutic";
      case 5:
        return "Diagnostic";
      case 6:
        return "Other";
      default:
        return "Unknown";
    }
  };

  const getChargeTypeText = (type) => {
    switch (type) {
      case 1:
        return "Hourly";
      case 2:
        return "Daily";
      case 3:
        return "Per Use";
      case 4:
        return "Fixed";
      default:
        return "Unknown";
    }
  };

  const equipment = equipmentData?.results || equipmentData || [];
  const totalCount = equipmentData?.count || equipment.length;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          Medical Equipment Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddEquipment}
          size="large"
        >
          Add Equipment
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <MedicalServices color="primary" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {totalCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Equipment
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <CheckCircle color="success" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {availableEquipment?.length || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Available
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Warning color="warning" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {inUseEquipment?.length || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    In Use
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <AttachMoney color="info" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {
                      equipment.filter((e) => e.status === 3 || e.status === 4)
                        .length
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Maintenance/Out of Order
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search equipment..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="1">Available</MenuItem>
                <MenuItem value="2">In Use</MenuItem>
                <MenuItem value="3">Maintenance</MenuItem>
                <MenuItem value="4">Out of Order</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Equipment Type</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="1">Respiratory</MenuItem>
                <MenuItem value="2">Cardiovascular</MenuItem>
                <MenuItem value="3">Monitoring</MenuItem>
                <MenuItem value="4">Therapeutic</MenuItem>
                <MenuItem value="5">Diagnostic</MenuItem>
                <MenuItem value="6">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("all");
                setTypeFilter("all");
                setPage(1);
              }}
              fullWidth
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Equipment Grid */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Equipment List
        </Typography>
        {equipmentLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={2}>
              {equipment.map((equipment) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={equipment.id}>
                  <Card sx={{ height: "100%" }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <MedicalServices color="primary" />
                        <Typography variant="h6" sx={{ flexGrow: 1 }}>
                          {equipment.name}
                        </Typography>
                        <Chip
                          label={getStatusText(equipment.status)}
                          size="small"
                          color={getStatusColor(equipment.status)}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        {equipment.description}
                      </Typography>
                      <Box display="flex" gap={1} mb={2}>
                        <Chip
                          label={getEquipmentTypeText(equipment.equipment_type)}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                        <Chip
                          label={getChargeTypeText(equipment.charge_type)}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color="primary"
                        mb={2}
                      >
                        Rate: {equipment.current_rate} AFN (
                        {getChargeTypeText(equipment.charge_type).toLowerCase()}
                        )
                      </Typography>
                      {equipment.location && (
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <LocationOn fontSize="small" color="action" />
                          <Typography variant="caption">
                            {equipment.location}
                          </Typography>
                        </Box>
                      )}
                      {equipment.serial_number && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mb={2}
                        >
                          Serial: {equipment.serial_number}
                        </Typography>
                      )}
                      <Box display="flex" gap={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Edit />}
                          onClick={() => handleEditEquipment(equipment)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<Delete />}
                          onClick={() => {
                            if (
                              window.confirm(
                                `Are you sure you want to delete ${equipment.name}?`
                              )
                            ) {
                              deleteEquipmentMutation.mutate(equipment.id);
                            }
                          }}
                          disabled={deleteEquipmentMutation.isPending}
                        >
                          Delete
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Pagination */}
            {totalCount > rowsPerPage && (
              <Box display="flex" justifyContent="center" mt={3}>
                <Pagination
                  count={Math.ceil(totalCount / rowsPerPage)}
                  page={page}
                  onChange={(event, value) => setPage(value)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Add/Edit Equipment Dialog */}
      <Dialog
        open={equipmentDialogOpen}
        onClose={() => setEquipmentDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedEquipment ? "Edit Equipment" : "Add New Equipment"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Equipment Name"
                value={equipmentFormData.name}
                onChange={(e) =>
                  setEquipmentFormData({
                    ...equipmentFormData,
                    name: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={equipmentFormData.description}
                onChange={(e) =>
                  setEquipmentFormData({
                    ...equipmentFormData,
                    description: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Equipment Type</InputLabel>
                <Select
                  value={equipmentFormData.equipment_type}
                  onChange={(e) =>
                    setEquipmentFormData({
                      ...equipmentFormData,
                      equipment_type: e.target.value,
                    })
                  }
                >
                  <MenuItem value={1}>Respiratory</MenuItem>
                  <MenuItem value={2}>Cardiovascular</MenuItem>
                  <MenuItem value={3}>Monitoring</MenuItem>
                  <MenuItem value={4}>Therapeutic</MenuItem>
                  <MenuItem value={5}>Diagnostic</MenuItem>
                  <MenuItem value={6}>Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Charge Type</InputLabel>
                <Select
                  value={equipmentFormData.charge_type}
                  onChange={(e) =>
                    setEquipmentFormData({
                      ...equipmentFormData,
                      charge_type: e.target.value,
                    })
                  }
                >
                  <MenuItem value={1}>Hourly</MenuItem>
                  <MenuItem value={2}>Daily</MenuItem>
                  <MenuItem value={3}>Per Use</MenuItem>
                  <MenuItem value={4}>Fixed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Hourly Rate (AFN)"
                type="number"
                value={equipmentFormData.hourly_rate}
                onChange={(e) =>
                  setEquipmentFormData({
                    ...equipmentFormData,
                    hourly_rate: parseFloat(e.target.value) || 0,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Daily Rate (AFN)"
                type="number"
                value={equipmentFormData.daily_rate}
                onChange={(e) =>
                  setEquipmentFormData({
                    ...equipmentFormData,
                    daily_rate: parseFloat(e.target.value) || 0,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Per Use Rate (AFN)"
                type="number"
                value={equipmentFormData.per_use_rate}
                onChange={(e) =>
                  setEquipmentFormData({
                    ...equipmentFormData,
                    per_use_rate: parseFloat(e.target.value) || 0,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fixed Rate (AFN)"
                type="number"
                value={equipmentFormData.fixed_rate}
                onChange={(e) =>
                  setEquipmentFormData({
                    ...equipmentFormData,
                    fixed_rate: parseFloat(e.target.value) || 0,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={equipmentFormData.location}
                onChange={(e) =>
                  setEquipmentFormData({
                    ...equipmentFormData,
                    location: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Serial Number"
                value={equipmentFormData.serial_number}
                onChange={(e) =>
                  setEquipmentFormData({
                    ...equipmentFormData,
                    serial_number: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={equipmentFormData.notes}
                onChange={(e) =>
                  setEquipmentFormData({
                    ...equipmentFormData,
                    notes: e.target.value,
                  })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEquipmentDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitEquipment}
            disabled={
              createEquipmentMutation.isPending ||
              updateEquipmentMutation.isPending
            }
          >
            {selectedEquipment ? "Update" : "Add"} Equipment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
          zIndex: 1000,
        }}
        onClick={handleAddEquipment}
      >
        <Add />
      </Fab>
    </Box>
  );
};

export default MedicalEquipmentPage;
